import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProvider,
  rotateProviderKey,
  deleteSession,
  type Provider,
  type ProviderWithSessions,
  type Session,
} from "../api/client";
import { StatusBadge } from "../components/StatusBadge";

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDateShort(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function providerStatus(p: Provider): { label: string; cls: string } {
  if (!p.active) return { label: "Inattivo", cls: "badge-neutral" };
  if (p.expires_at && new Date(p.expires_at) < new Date())
    return { label: "Scaduto", cls: "badge-danger" };
  return { label: "Attivo", cls: "badge-success" };
}

function ApiKeyBox({ apiKey }: { apiKey: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  return (
    <div style={{ display: "flex", gap: "var(--space-2)" }}>
      <input
        className="form-input"
        readOnly
        value={apiKey}
        style={{ fontFamily: "monospace", fontSize: "var(--font-size-sm)" }}
      />
      <button
        className="btn btn-outline"
        onClick={copy}
        style={{ flexShrink: 0 }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
          {copied ? "check" : "content_copy"}
        </span>
        {copied ? "Copiato!" : "Copia"}
      </button>
    </div>
  );
}

function SessionsTable({
  sessions,
  onRowClick,
  onDelete,
}: {
  sessions: Session[];
  onRowClick: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  if (sessions.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "var(--space-8)", color: "var(--indaco-gray-500)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 40, display: "block", marginBottom: "var(--space-2)" }}>
          folder_open
        </span>
        Nessuna sessione per questo provider
      </div>
    );
  }

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>Data</th>
            <th>Destinatario</th>
            <th>Stato</th>
            <th>Riconoscimento</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.id} onClick={() => onRowClick(s.id)} style={{ cursor: "pointer" }}>
              <td style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", whiteSpace: "nowrap" }}>
                {fmtDate(s.created_at)}
              </td>
              <td style={{ fontWeight: 500 }}>{s.recipient_name}</td>
              <td><StatusBadge status={s.status} /></td>
              <td style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)" }}>
                {s.recognition_type || "—"}
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => onDelete(s.id)}
                  title="Elimina sessione"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RotateKeyDialog({
  provider,
  onConfirm,
  onCancel,
  rotating,
  rotateError,
}: {
  provider: Provider;
  onConfirm: () => void;
  onCancel: () => void;
  rotating: boolean;
  rotateError: string;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !rotating) onCancel(); }}
    >
      <div
        className="card"
        style={{ maxWidth: 480, width: "90%", boxShadow: "var(--shadow-lg)" }}
      >
        <div
          style={{
            display: "flex",
            gap: "var(--space-3)",
            marginBottom: "var(--space-4)",
            alignItems: "flex-start",
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{
              color: "var(--indaco-warning)",
              fontSize: 28,
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            warning
          </span>
          <div>
            <h3
              style={{
                margin: "0 0 var(--space-2)",
                fontSize: "var(--font-size-md)",
                color: "var(--indaco-brand-dark)",
              }}
            >
              Ruota chiave API
            </h3>
            <p
              style={{
                fontSize: "var(--font-size-sm)",
                color: "var(--indaco-gray-600)",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              La chiave attuale (
              <code style={{ fontFamily: "monospace" }}>
                {provider.api_key_prefix}…
              </code>
              ) <strong>smetterà immediatamente di funzionare</strong>. Tutte
              le integrazioni che la utilizzano dovranno essere aggiornate con
              la nuova chiave.
            </p>
          </div>
        </div>

        {rotateError && (
          <div
            className="toast toast-error"
            style={{ marginBottom: "var(--space-3)" }}
          >
            <span className="material-symbols-outlined">error</span>
            {rotateError}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "var(--space-2)",
            justifyContent: "flex-end",
          }}
        >
          <button
            className="btn btn-outline"
            onClick={onCancel}
            disabled={rotating}
          >
            Annulla
          </button>
          <button
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={rotating}
          >
            {rotating ? (
              <>
                <span className="spinner" /> Rotazione…
              </>
            ) : (
              <>
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 18 }}
                >
                  refresh
                </span>
                Ruota chiave
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ProviderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [provider, setProvider] = useState<ProviderWithSessions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const [showRotateDialog, setShowRotateDialog] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState("");
  const [newApiKey, setNewApiKey] = useState<string | null>(null);

  const fetchProvider = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getProvider(id);
      setProvider(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProvider();
    timerRef.current = setInterval(fetchProvider, 10_000);
    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(timerRef.current);
      } else {
        fetchProvider();
        timerRef.current = setInterval(fetchProvider, 10_000);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchProvider]);

  const handleRotateKey = async () => {
    if (!id) return;
    setRotating(true);
    setRotateError("");
    try {
      const result = await rotateProviderKey(id);
      setNewApiKey(result.api_key);
      setShowRotateDialog(false);
      fetchProvider();
    } catch (e) {
      setRotateError((e as Error).message);
    } finally {
      setRotating(false);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!confirm("Eliminare questa sessione? L'azione è irreversibile.")) return;
    try {
      await deleteSession(sessionId);
      fetchProvider();
    } catch (e) {
      alert("Errore: " + (e as Error).message);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "var(--space-8)",
        }}
      >
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/providers")}
          style={{ marginBottom: "var(--space-3)" }}
        >
          <span className="material-symbols-outlined">arrow_back</span> Torna
          ai provider
        </button>
        <div className="toast toast-error">
          <span className="material-symbols-outlined">error</span>
          {error || "Provider non trovato"}
        </div>
      </div>
    );
  }

  const status = providerStatus(provider);

  return (
    <div>
      <button
        className="btn btn-ghost"
        onClick={() => navigate("/providers")}
        style={{ marginBottom: "var(--space-4)" }}
      >
        <span className="material-symbols-outlined">arrow_back</span> Torna ai
        provider
      </button>

      {/* New key banner (visible immediately after rotation) */}
      {newApiKey && (
        <div
          style={{
            background: "var(--indaco-warning-light)",
            border: "1px solid var(--indaco-warning)",
            borderRadius: "var(--border-radius-md)",
            padding: "var(--space-4)",
            marginBottom: "var(--space-4)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "var(--space-3)",
              alignItems: "flex-start",
              marginBottom: "var(--space-3)",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ color: "var(--indaco-warning)", flexShrink: 0 }}
            >
              warning
            </span>
            <div>
              <strong style={{ display: "block", marginBottom: 4 }}>
                Nuova chiave API generata
              </strong>
              <p
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--indaco-gray-700)",
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Salvala ora — non sarà più visibile dopo aver ricaricato la
                pagina.
              </p>
            </div>
          </div>
          <ApiKeyBox apiKey={newApiKey} />
        </div>
      )}

      {/* Header card */}
      <div className="card">
        <div className="card-header">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              flexWrap: "wrap",
            }}
          >
            <h1
              className="section-title"
              style={{ margin: 0, fontSize: "var(--font-size-lg)" }}
            >
              {provider.name}
            </h1>
            <span className={`badge ${status.cls}`}>{status.label}</span>
          </div>
          <button
            className="btn btn-outline"
            onClick={() => setShowRotateDialog(true)}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 18 }}
            >
              refresh
            </span>
            Ruota chiave API
          </button>
        </div>

        {provider.description && (
          <p
            style={{
              fontSize: "var(--font-size-sm)",
              color: "var(--indaco-gray-600)",
              margin: "0 0 var(--space-4)",
              lineHeight: 1.5,
            }}
          >
            {provider.description}
          </p>
        )}

        <div className="detail-grid">
          <div>
            <span className="detail-label">ID</span>
            <span
              className="detail-value"
              style={{
                fontFamily: "monospace",
                fontSize: "var(--font-size-xs)",
              }}
            >
              {provider.id}
            </span>
          </div>

          <div>
            <span className="detail-label">Prodotti</span>
            <div
              style={{
                display: "flex",
                gap: 4,
                flexWrap: "wrap",
                marginTop: 4,
              }}
            >
              {provider.allowed_products.map((p) => (
                <span
                  key={p}
                  className="badge"
                  style={{
                    background:
                      p.toLowerCase() === "top"
                        ? "var(--indaco-info-light)"
                        : "#F3E8FF",
                    color:
                      p.toLowerCase() === "top"
                        ? "var(--indaco-info)"
                        : "#7C3AED",
                  }}
                >
                  {p.toUpperCase()}
                </span>
              ))}
            </div>
          </div>

          <div>
            <span className="detail-label">Tipi di riconoscimento</span>
            <div
              style={{
                display: "flex",
                gap: 4,
                flexWrap: "wrap",
                marginTop: 4,
              }}
            >
              {provider.allowed_recognition_types.map((rt) => (
                <span
                  key={rt}
                  className="badge badge-neutral"
                  style={{ fontSize: "0.65rem" }}
                >
                  {rt.replace("FEA_", "")}
                </span>
              ))}
            </div>
          </div>

          <div>
            <span className="detail-label">API key prefix</span>
            <span
              className="detail-value"
              style={{
                fontFamily: "monospace",
                fontSize: "var(--font-size-xs)",
                color: "var(--indaco-gray-600)",
              }}
            >
              {provider.api_key_prefix}…
            </span>
          </div>

          {provider.webhook_url && (
            <div>
              <span className="detail-label">Webhook URL</span>
              <span
                className="detail-value"
                style={{
                  fontFamily: "monospace",
                  fontSize: "var(--font-size-xs)",
                  wordBreak: "break-all",
                }}
              >
                {provider.webhook_url}
              </span>
            </div>
          )}

          <div>
            <span className="detail-label">Scadenza</span>
            <span className="detail-value">
              {provider.expires_at
                ? fmtDateShort(provider.expires_at)
                : "Nessuna scadenza"}
            </span>
          </div>

          <div>
            <span className="detail-label">Sessioni totali</span>
            <span className="detail-value">{provider.session_count}</span>
          </div>

          <div>
            <span className="detail-label">Ultima sessione</span>
            <span className="detail-value">
              {fmtDateShort(provider.last_session_at)}
            </span>
          </div>

          <div>
            <span className="detail-label">Creato il</span>
            <span className="detail-value">{fmtDate(provider.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Sessions */}
      <div
        className="card"
        style={{ marginTop: "var(--space-4)", padding: 0, overflow: "hidden" }}
      >
        <div
          style={{
            padding: "var(--space-4)",
            borderBottom: "1px solid var(--indaco-gray-200)",
          }}
        >
          <h2 className="section-title" style={{ margin: 0 }}>
            Sessioni
          </h2>
        </div>
        <SessionsTable
          sessions={provider.sessions}
          onRowClick={(sid) => navigate(`/sessions/${sid}`, { state: { returnTo: `/providers/${id}` } })}
          onDelete={handleDeleteSession}
        />
      </div>

      {/* Key rotation dialog */}
      {showRotateDialog && (
        <RotateKeyDialog
          provider={provider}
          onConfirm={handleRotateKey}
          onCancel={() => {
            setShowRotateDialog(false);
            setRotateError("");
          }}
          rotating={rotating}
          rotateError={rotateError}
        />
      )}
    </div>
  );
}
